import { geoDist } from './geo';

// turn-by-turn navigation along a guide route (OSRM steps), with voice texts and rerouting
const OSRM = (prof) => `https://routing.openstreetmap.de/routed-${prof === 'foot' ? 'foot' : 'bike'}/route/v1/driving`;

export const mapSteps = (legs) => (legs || []).flatMap((l) => l.steps || []).map((s) => ({ lat: s.maneuver.location[1], lon: s.maneuver.location[0], type: s.maneuver.type, mod: s.maneuver.modifier || '', name: s.name || '', exit: s.maneuver.exit }));

export async function fetchRoute(a, b, prof) {
  const r = await (await fetch(`${OSRM(prof)}/${a[1]},${a[0]};${b[1]},${b[0]}?overview=full&geometries=geojson&steps=true`)).json();
  const rt = r?.routes?.[0];
  if (!rt) throw new Error('route');
  return { pts: rt.geometry.coordinates.map(([lon, lat]) => [lat, lon]), steps: mapSteps(rt.legs), km: rt.distance / 1000 };
}

// densify so that no segment is longer than ~12 m (nearest-vertex search stays accurate)
const dense = (pts) => pts.flatMap((p, i) => {
  if (!i) return [p];
  const q = pts[i - 1], n = Math.ceil(geoDist(q, p) / 12);
  return Array.from({ length: n }, (_, k) => [q[0] + ((p[0] - q[0]) * (k + 1)) / n, q[1] + ((p[1] - q[1]) * (k + 1)) / n]);
});
export function buildNav(raw, steps) {
  const pts = dense(raw);
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + geoDist(pts[i - 1], pts[i]));
  const near = (p) => { let bi = 0, bd = Infinity; pts.forEach((q, i) => { const d = geoDist(p, q); if (d < bd) { bd = d; bi = i; } }); return bi; };
  return { pts, cum, total: cum[cum.length - 1], steps: (steps || []).map((s) => ({ ...s, at: cum[near([s.lat, s.lon])] })).filter((s) => s.type !== 'depart') };
}

// position on the route: progress (m), distance off the route (m), next manoeuvre
export function navState(nav, p, hint = 0) {
  const { pts, cum } = nav;
  let bi = 0, bd = Infinity;
  const from = Math.max(0, hint - 60), to = Math.min(pts.length, hint + 600);
  for (let i = from; i < to; i++) { const d = geoDist([p.lat, p.lon], pts[i]); if (d < bd) { bd = d; bi = i; } }
  if (bd > 150) pts.forEach((q, i) => { const d = geoDist([p.lat, p.lon], q); if (d < bd) { bd = d; bi = i; } });
  const prog = cum[bi], next = nav.steps.find((s) => s.at > prog + 5) || null;
  return { idx: bi, off: bd, prog, left: nav.total - prog, next, dist: next ? next.at - prog : nav.total - prog };
}

export const stepKey = (s) => (s.type === 'arrive' ? 'arrive' : s.type === 'roundabout' || s.type === 'rotary' ? 'roundabout' : s.mod.replace(' ', '_') || 'straight');
export const stepText = (t, s, dist) => {
  const k = stepKey(s);
  const d = dist >= 1000 ? `${(dist / 1000).toFixed(1).replace('.', t.locale?.startsWith('en') ? '.' : ',')} km` : `${Math.max(10, Math.round(dist / 10) * 10)} m`;
  if (k === 'arrive') return t.nvArrIn.replace('{d}', d);
  return t.nvIn.replace('{d}', d).replace('{a}', (t['nv_' + k] || t.nv_straight).replace('{n}', s.exit || '')) + (s.name && k !== 'arrive' ? ` – ${s.name}` : '');
};
